import { useRef, useState } from "react";
import { useTextos } from "../context/useTextos";
import { useSettings } from "../context/useSettings";
import { PALETAS_CORES, PAPEIS_FUNDO, DEFAULT_WALLPAPER_KEY } from "../lib/personalizacao";

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ConfigPage() {
  const { t } = useTextos();
  const { settings, updateSettings, isFieldControlled, isFeatureControlled } = useSettings();
  const fileInputRef = useRef(null);
  const [salvo, setSalvo] = useState(false);

  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    updateSettings({ logoUrl: dataUrl });
    flashSalvo();
  }

  function flashSalvo() {
    setSalvo(true);
    setTimeout(() => setSalvo(false), 1500);
  }

  function handleCorChange(e) {
    updateSettings({ primaryColor: e.target.value });
    flashSalvo();
  }

  function handleFundoChange(e) {
    updateSettings({ backgroundColor: e.target.value });
    flashSalvo();
  }

  function handleTemaChange(tema) {
    updateSettings({ theme: tema });
    flashSalvo();
  }

  function handlePaletaClick(paleta) {
    updateSettings({ primaryColor: paleta.primaryColor, backgroundColor: paleta.backgroundColor });
    flashSalvo();
  }

  function handlePapelFundoClick(papel) {
    updateSettings({ wallpaperKey: papel.key });
    flashSalvo();
  }

  function handleFeatureToggle(feature) {
    updateSettings({ features: { ...settings.features, [feature]: !settings.features[feature] } });
    flashSalvo();
  }

  return (
    <div className="page">
      <header className="page__header">
        <h2>{t("checklist.cfg_titulo")}</h2>
        {salvo && <span className="hint hint--ok">{t("checklist.cfg_salvo")}</span>}
      </header>

      <section className="form-section">
        <h3>{t("checklist.cfg_identidade_visual")}</h3>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_logo")}</strong>
            <p className="hint">{t("checklist.cfg_logo_hint")}</p>
          </div>
          <div className="config-row__control">
            {settings.logoUrl && (
              <img src={settings.logoUrl} alt={t("checklist.cfg_logo_alt")} className="config-logo-preview" />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={isFieldControlled("logoUrl")}
            />
            {isFieldControlled("logoUrl") && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel")}</p>
            )}
          </div>
        </div>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_cor_principal")}</strong>
            <p className="hint">{t("checklist.cfg_cor_principal_hint")}</p>
          </div>
          <div className="config-row__control">
            <input
              type="color"
              value={settings.primaryColor}
              onChange={handleCorChange}
              disabled={isFieldControlled("primaryColor")}
            />
            {isFieldControlled("primaryColor") && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel_fem")}</p>
            )}
          </div>
        </div>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_cor_fundo")}</strong>
            <p className="hint">{t("checklist.cfg_cor_fundo_hint")}</p>
          </div>
          <div className="config-row__control">
            <input
              type="color"
              value={settings.backgroundColor}
              onChange={handleFundoChange}
              disabled={isFieldControlled("backgroundColor")}
            />
            {isFieldControlled("backgroundColor") && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel_fem")}</p>
            )}
          </div>
        </div>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_tema")}</strong>
            <p className="hint">{t("checklist.cfg_tema_hint")}</p>
          </div>
          <div className="config-row__control">
            <div className="theme-toggle">
              <button
                type="button"
                className={`btn btn--small ${settings.theme === "light" ? "btn--primary" : "btn--secondary"}`}
                onClick={() => handleTemaChange("light")}
                disabled={isFieldControlled("theme")}
              >
                {t("checklist.cfg_claro")}
              </button>
              <button
                type="button"
                className={`btn btn--small ${settings.theme === "dark" ? "btn--primary" : "btn--secondary"}`}
                onClick={() => handleTemaChange("dark")}
                disabled={isFieldControlled("theme")}
              >
                {t("checklist.cfg_escuro")}
              </button>
            </div>
            {isFieldControlled("theme") && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel")}</p>
            )}
          </div>
        </div>
      </section>

      <section className="form-section">
        <h3>{t("checklist.cfg_personalizacao")}</h3>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_paletas")}</strong>
            <p className="hint">{t("checklist.cfg_paletas_hint")}</p>
          </div>
          <div className="config-row__control">
            <div className="swatch-grid">
              {PALETAS_CORES.map((paleta) => (
                <button
                  key={paleta.key}
                  type="button"
                  className={`swatch ${
                    settings.primaryColor === paleta.primaryColor &&
                    settings.backgroundColor === paleta.backgroundColor
                      ? "swatch--ativo"
                      : ""
                  }`}
                  style={{ backgroundImage: "none", backgroundColor: paleta.primaryColor }}
                  title={paleta.label}
                  onClick={() => handlePaletaClick(paleta)}
                  disabled={isFieldControlled("primaryColor") || isFieldControlled("backgroundColor")}
                >
                  <span className="sr-only">{paleta.label}</span>
                </button>
              ))}
            </div>
            {(isFieldControlled("primaryColor") || isFieldControlled("backgroundColor")) && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel")}</p>
            )}
          </div>
        </div>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_papel_fundo")}</strong>
            <p className="hint">{t("checklist.cfg_papel_fundo_hint")}</p>
          </div>
          <div className="config-row__control">
            <div className="swatch-grid">
              {PAPEIS_FUNDO.map((papel) => (
                <button
                  key={papel.key}
                  type="button"
                  className={`swatch swatch--${papel.key} ${
                    (settings.wallpaperKey || DEFAULT_WALLPAPER_KEY) === papel.key ? "swatch--ativo" : ""
                  }`}
                  title={papel.label}
                  onClick={() => handlePapelFundoClick(papel)}
                  disabled={isFieldControlled("wallpaperKey")}
                >
                  <span className="sr-only">{papel.label}</span>
                </button>
              ))}
            </div>
            {isFieldControlled("wallpaperKey") && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel")}</p>
            )}
          </div>
        </div>
      </section>

      <section className="form-section">
        <h3>{t("checklist.cfg_funcionalidades")}</h3>

        <div className="config-row">
          <div className="config-row__label">
            <strong>{t("checklist.cfg_cadastro_clientes_veiculos")}</strong>
            <p className="hint">{t("checklist.cfg_cadastro_clientes_veiculos_hint")}</p>
          </div>
          <div className="config-row__control">
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.features.clientesVeiculos}
                onChange={() => handleFeatureToggle("clientesVeiculos")}
                disabled={isFeatureControlled("clientesVeiculos")}
              />
              <span className="switch__track" />
            </label>
            {isFeatureControlled("clientesVeiculos") && (
              <p className="hint hint--controlled">{t("checklist.cfg_controlado_painel")}</p>
            )}
          </div>
        </div>
      </section>

      <p className="hint">
        {t("checklist.cfg_rodape_p1")}{" "}
        <code>config</code> {t("checklist.cfg_rodape_p2")} <code>WorkshopProvider</code>
        {t("checklist.cfg_rodape_p3")}
      </p>
    </div>
  );
}
